/**
 * 地图面板 —— 计划分区里的地图这一格。
 *
 * ## 职责划分
 *
 * 这个组件只做三件事：把运行时遥测喂给引擎、把引擎的快照画成中文文案、把按钮接到引擎的动作上。
 * 它**不**做相机决策（那是 `engine.ts`），也**不**碰 MapLibre（那是 `MapCanvas.tsx`）。
 *
 * ## 为什么按钮用 `DisabledReason` 而不是 `disabled`
 *
 * "定位到机体"最容易被点到的时刻恰恰是"还没有位置"的时候。原生 `disabled` 的按钮
 * 连悬停事件都收不到，于是用户唯一的结论是"这个按钮坏了"。所以不可用时按钮仍然可聚焦、
 * 可悬停，并明确说出原因（"尚未收到位置数据"）。
 *
 * ## 为什么不画到 (0,0)
 *
 * 位置为 `null` 时**不放置任何标记**（数据层返回空集合，见 `layers.ts`）。
 * 画到 (0,0) 会显示在几内亚湾 —— 用户会以为机体在那里，而实际上只是还没定位。
 * 界面上对应的说法是"尚未收到位置数据"，两处必须一致。
 *
 * ## 后续接入
 *
 * 航点编辑（新增/拖动/删除、批量改高度与速度）、地理围栏与返航点编辑**不在**本文件里，
 * 由 `plan-waypoint-editor` 填充：它们需要先把编辑结果上传到机体，而那依赖 mission 管理器。
 * 画出一堆不能上传的假航点只会让人以为功能已经好了。
 */

import {
  Badge,
  Button,
  DisabledReason,
  Panel,
  Select,
  Switch,
  useConfirm,
  type ConfirmFn,
  type StatusLevel,
} from '@hgc/ui';
import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense, type JSX } from 'react';

import { useVehicleState } from '../runtime/hooks';
import { formatBytes, formatLatLon } from './coverage';
import { MapEngine, type MapSnapshot, type MapSurface } from './engine';
import {
  defaultSourceCatalog,
  readSourceKey,
  sourceReadiness,
  type BaseMapSource,
} from './sources';
import {
  readFollowPreference,
  readOfflineOnlyPreference,
  readSourcePreference,
  writeFollowPreference,
  writeOfflineOnlyPreference,
  writeSourcePreference,
} from './settings';
import { describeEvents } from './tileProtocol';
import { useTileService, type TileService } from './tileService';
import { TILE_CACHE_LIMITS, type TileCacheStats } from './tileStore';

/** 位置状态 → 界面等级。缺定位是**异常**，必须用鲜亮语义色，不能用灰。 */
const POSITION_LEVELS: Readonly<Record<string, StatusLevel>> = {
  ok: 'ok',
  'no-data': 'warning',
  invalid: 'critical',
  'no-fix': 'critical',
};

const POSITION_LABELS: Readonly<Record<string, string>> = {
  ok: '位置可用',
  'no-data': '尚未收到位置',
  invalid: '坐标无效',
  'no-fix': 'GPS 未定位',
};

/**
 * 地图库按需加载。
 *
 * MapLibre 一个人就往产物里塞进约 1.5 MB 的代码，而地图只出现在**计划**分区 ——
 * 让"飞行"分区（最需要快速出数的那一屏）为它付首屏代价不划算。
 * 按需加载之后，只有真正打开计划分区才会去取这一块。
 */
const MapCanvas = lazy(async () => {
  const module = await import('./MapCanvas');
  return { default: module.MapCanvas };
});

/** 底图块加载中的占位。刻意写明"在加载"，而不是留一块空白让人以为地图坏了。 */
function MapLoading(): JSX.Element {
  return (
    <div className="flex min-h-[320px] flex-1 items-center justify-center rounded-card border border-line bg-surface-2 text-xs text-fg-subtle">
      地图组件加载中…
    </div>
  );
}

export function MapPanel(): JSX.Element {
  const state = useVehicleState();
  const tileService = useTileService();
  const confirm = useConfirm();

  // `list()` 每次都返回新数组；不固定下来的话下面依赖它的 effect 每渲染一次就会重建引擎
  const sources = useMemo(() => defaultSourceCatalog.list(), []);
  const [sourceId, setSourceId] = useState(() => readSourcePreference(sources));
  const [offlineOnly, setOfflineOnly] = useState(() => readOfflineOnlyPreference());
  /*
   * 初始跟随偏好用一个"只取第一次"的状态放着：它只在引擎创建时读一次，
   * 之后跟随的真实状态以引擎为准（用户拖动地图会让引擎自己暂停，那个状态也要能显示出来）。
   */
  const [followAtBoot] = useState(() => readFollowPreference());

  const [surface, setSurface] = useState<MapSurface | null>(null);
  const [snapshot, setSnapshot] = useState<MapSnapshot | null>(null);
  const [stats, setStats] = useState<TileCacheStats | null>(null);
  /** 地图起不来时的中文原因（无 WebGL、容器尺寸为 0…）。空串/none 之外的失败都要说出来。 */
  const [mapError, setMapError] = useState<string | null>(null);

  const engineRef = useRef<MapEngine | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;
  const sourceIdRef = useRef(sourceId);
  sourceIdRef.current = sourceId;

  const source = useMemo(
    () => defaultSourceCatalog.get(sourceId) ?? sources[0],
    [sourceId, sources],
  );

  /* ── 引擎生命周期：地图就绪即接线，卸载即断开 ── */
  useEffect(() => {
    if (surface === null) {
      engineRef.current = null;
      setSnapshot(null);
      return undefined;
    }
    const initial = defaultSourceCatalog.get(sourceIdRef.current) ?? sources[0];
    if (initial === undefined) return undefined;

    const engine = new MapEngine({ surface, source: initial, follow: followAtBoot });
    engineRef.current = engine;
    engine.update(stateRef.current);
    setSnapshot(engine.snapshotOf());
    const unsubscribe = engine.subscribe(() => setSnapshot(engine.snapshotOf()));
    return () => {
      unsubscribe();
      engine.dispose();
      engineRef.current = null;
    };
  }, [surface, sources, followAtBoot]);

  /* ── 遥测：每来一帧推给引擎（引擎内部有闸门，不会每帧重绘） ── */
  useEffect(() => {
    engineRef.current?.update(state);
  }, [state]);

  /* ── 底图源：换的是描述，车辆图层一行都不动 ── */
  useEffect(() => {
    const engine = engineRef.current;
    if (engine === null) return;
    const next = defaultSourceCatalog.get(sourceId);
    if (next !== undefined) engine.setSource(next);
  }, [sourceId]);

  /* ── 离线模式 ── */
  useEffect(() => {
    writeOfflineOnlyPreference(offlineOnly);
    tileService?.setMode(offlineOnly ? 'cache-only' : 'online');
  }, [offlineOnly, tileService]);

  const refreshStats = useCallback(async (): Promise<void> => {
    if (tileService === null) return;
    setStats(await tileService.stats());
  }, [tileService]);

  useEffect(() => {
    void refreshStats();
  }, [refreshStats]);

  /*
   * 瓦片在浏览过程中不断进缓存，占用数字不能停在"打开这一格"的那一刻 ——
   * 那会让人以为"什么都没缓存上"，而他会据此决定要不要在出发前再联网一次。
   * 用尾随节流（1 秒）刷新：`stats()` 要读整张元数据表，每个瓦片都触发一次是浪费。
   */
  useEffect(() => {
    if (tileService === null) return undefined;
    let timer: number | null = null;
    const unsubscribe = tileService.onEvent(() => {
      if (timer !== null) return;
      timer = window.setTimeout(() => {
        timer = null;
        void refreshStats();
      }, 1000);
    });
    return () => {
      unsubscribe();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [tileService, refreshStats]);

  const onUserGesture = useCallback((originalEvent: unknown): void => {
    engineRef.current?.onMapMove(originalEvent);
  }, []);

  const position = snapshot?.position ?? null;
  const positionLevel = POSITION_LEVELS[position?.status ?? 'no-data'] ?? 'warning';
  const following = snapshot?.follow.mode === 'following';

  /**
   * 两个动作各自的不可用原因。优先说"地图还没就绪"：那种情况下连原因都没有
   * （按钮按下去什么都没发生），比"没有位置"更需要解释。
   */
  const mapBlocked =
    mapError !== null ? '地图初始化失败' : snapshot === null || !snapshot.ready ? '地图正在加载' : null;
  const locateReason =
    mapBlocked ?? (snapshot !== null && !snapshot.canLocate ? position?.note ?? '尚未收到位置数据' : undefined);
  const overviewReason =
    mapBlocked ??
    (snapshot !== null && !snapshot.canOverview
      ? '还没有可全览的内容：航迹至少要有两个点，或先收到位置'
      : undefined);
  const clearReason = clearBlockedReason(tileService);

  return (
    <Panel
      title="地图"
      description="机体位置、航迹与底图。坐标缺失时不放置任何标记。"
      fill
    >
      <div className="flex h-full min-h-0 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <label className="flex items-center gap-1.5 text-xs text-fg-muted" htmlFor="map-source">
            底图
            <Select
              id="map-source"
              aria-label="底图源"
              size="sm"
              value={sourceId}
              options={sources.map((candidate) => ({
                value: candidate.id,
                label: candidate.requiresKey
                  ? `${candidate.name}（需要密钥）`
                  : candidate.name,
                // 需要密钥又没填的源直接不可选：让用户选中它再报"没有密钥"是两步
                disabled: !sourceReadiness(candidate, readSourceKey(candidate.id)).ready,
              }))}
              onChange={(next) => {
                setSourceId(next);
                writeSourcePreference(next);
              }}
            />
          </label>

          <DisabledReason reason={locateReason}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                engineRef.current?.locate();
              }}
            >
              定位到机体
            </Button>
          </DisabledReason>

          <DisabledReason reason={overviewReason}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                engineRef.current?.overview();
              }}
            >
              全览航迹
            </Button>
          </DisabledReason>

          <Switch
            size="sm"
            value={following}
            label="跟随机体"
            onChange={(next) => {
              writeFollowPreference(next);
              engineRef.current?.setFollow(next);
            }}
          />
        </div>

        {/* 跟随暂停必须说出来 —— "为什么视野不跟着动了"不能没有答案 */}
        {snapshot === null || snapshot.hint === null ? null : (
          <div className="flex items-center gap-2 rounded-control border border-line bg-caution-soft px-2 py-1">
            <Badge size="sm" level="caution" variant="outline">
              跟随已暂停
            </Badge>
            <span className="text-xs text-caution">{snapshot.hint}</span>
          </div>
        )}

        {mapError === null ? (
          <Suspense fallback={<MapLoading />}>
            <MapCanvas
              className="min-h-[320px] flex-1"
              onSurface={setSurface}
              onUserGesture={onUserGesture}
              onError={setMapError}
            />
          </Suspense>
        ) : (
          <div
            role="alert"
            className="flex min-h-[320px] flex-1 items-center rounded-card border border-critical bg-critical-soft px-3 py-2 text-xs text-critical"
          >
            {mapError}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <Badge size="sm" level={positionLevel} variant="soft" dot>
            {POSITION_LABELS[position?.status ?? 'no-data'] ?? '位置状态未知'}
          </Badge>
          {position === null || position.point === null ? (
            <span className="text-critical">{position?.note ?? '尚未收到位置数据'}</span>
          ) : (
            <span className="text-fg-muted">{formatLatLon(position.point)}</span>
          )}
          <span className="text-fg-subtle">·</span>
          <span className="text-fg-muted">
            航迹 {snapshot?.trailCount ?? 0} 点 / {formatTrailLength(snapshot?.trailLengthM ?? 0)}
          </span>
          <span className="text-fg-subtle">·</span>
          <span className="text-fg-muted">
            返航点 {snapshot === null || snapshot.home === null ? '未知' : formatLatLon(snapshot.home)}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line pt-2 text-xs">
          <Switch
            size="sm"
            value={offlineOnly}
            label="仅用已缓存地图"
            onChange={(next) => setOfflineOnly(next)}
          />
          <span className="text-fg-muted">
            已缓存 {stats?.tiles ?? 0} 块 / {formatBytes(stats?.bytes ?? 0)}
            （上限 {TILE_CACHE_LIMITS.maxTiles} 块、{formatBytes(TILE_CACHE_LIMITS.maxBytes)}，超出按最久未使用淘汰）
          </span>
          <DisabledReason reason={clearReason}>
            <Button
              size="sm"
              variant="danger"
              onClick={(event) => {
                void clearCache(confirm, tileService, stats, event.currentTarget, refreshStats);
              }}
            >
              清除本机地图缓存
            </Button>
          </DisabledReason>
        </div>

        <div className="flex flex-col gap-0.5 text-[11px] text-fg-subtle">
          <span>{mapStatusLine(source, tileService)}</span>
          <span>{source?.attribution ?? ''}</span>
          {source?.licenseNote === undefined ? null : <span>{source.licenseNote}</span>}
          {tileService !== null && tileService.durabilityNote !== null ? (
            <span className="text-warning">{tileService.durabilityNote}</span>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}

/**
 * "清除缓存"此刻为什么不能按。
 *
 * 只剩一种情况：缓存还没打开。以前这里还有第二种 —— "统一确认弹窗还没接线"，
 * 因为那时 `useConfirm` 来自一个运行期探测的桥接层。桥接层已经删掉：
 * 确认弹窗的宿主现在挂在应用根部、整个应用只此一处，`useConfirm()` 拿不到宿主会**直接报错**
 * （见 `@hgc/ui` 的 `dialogHooks.ts`），所以"没有确认可用"这件事不再是一个需要界面兜底的状态，
 * 而是一个必须在开发时就暴露的配置错误。
 */
function clearBlockedReason(service: TileService | null): string | undefined {
  return service === null ? '地图缓存正在打开' : undefined;
}

/**
 * 清除本地瓦片缓存。
 *
 * 这是本面板**唯一**的危险动作，因此必须过统一确认弹窗：清掉之后野外没有网就再也
 * 拿不回那些瓦片了，而这件事发生在"野外的机器上"是最常见的场景。
 * 绝不退化成 `window.confirm`（那会出现两套确认交互，且其中一套不受危险等级策略约束）。
 */
async function clearCache(
  confirm: ConfirmFn,
  service: TileService | null,
  stats: TileCacheStats | null,
  anchor: Element | null,
  refresh: () => Promise<void>,
): Promise<void> {
  if (service === null) return;
  const accepted = await confirm({
    title: '清除本机地图缓存？',
    consequence:
      `将删除本机已下载的 ${stats?.tiles ?? 0} 块瓦片（约 ${formatBytes(stats?.bytes ?? 0)}）。` +
      '删除后这些区域在断网时没有任何底图可显示，必须重新联网才能取回。',
    confirmLabel: '清除缓存',
    danger: 'caution',
    anchor,
  });
  if (!accepted) return;
  await service.clear();
  await refresh();
}

/** 底图此刻的取图状态（中文一句话）。 */
function mapStatusLine(source: BaseMapSource | undefined, service: TileService | null): string {
  if (source === undefined) return '没有可用的底图源';
  if (service === null) return '地图缓存还没打开';
  const mode = service.mode() === 'cache-only' ? '仅用缓存' : '允许联网';
  return `底图：${source.name} · z${source.minZoom}–${source.maxZoom} · ${mode} · ${describeEvents(service.recentEvents())}`;
}

function formatTrailLength(lengthM: number): string {
  if (!Number.isFinite(lengthM) || lengthM <= 0) return '0 m';
  return lengthM >= 1000 ? `${(lengthM / 1000).toFixed(2)} km` : `${lengthM.toFixed(0)} m`;
}
